import React, { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import Header from "../home/header";
import UTILS from "../../utils/commons";
import { ApiClient } from "../../utils/api-client";
import defaultProductImage from "../../img/no_data.svg";
import { Loader, X } from "lucide-react";
import { AxiosError } from "axios";
import TransactionItem from "../transactions/transaction_item";


const ProductDetail = ( props ) => {
  let navigate = useNavigate();
  let location = useLocation();
  const p = location.state;
  const { id } = useParams();
  const loginState = JSON.parse( window.sessionStorage.getItem( "loginState" ) );
  const apiClient = new ApiClient( loginState ? loginState.accessToken : null );

  const [ product, setProduct ] = React.useState( p );
  const [ inventory, setInventory ] = React.useState( {} );
  const [ loading, setLoading ] = React.useState( false );
  const [ recentTransactions, setRecentTransactions ] = React.useState( [] );

  const getRecentTransactions = async ( productId ) => {
    try {
      const req = await apiClient.get( `/transactions`, {
        params: {
          productId: productId,
          limit: 10
        }
      } );
      if ( req.status === 200 ) {
        setRecentTransactions( req.data );
      }
      else {
        setRecentTransactions( [] );
      }
    }
    catch ( e ) {
      console.log( "Error fetching transaction detail", e );
      setRecentTransactions( [] );
      if ( e instanceof AxiosError && e.response && e.response.status === 401 ) {
        UTILS.signout();
        navigate( `/signin`, { replace: true, target: `/transactions/${id}` } );
      }
    }
    finally {
      setLoading( false );
    }
  }
  const fetchIssuedInventory = async ( pid ) => {
    setLoading( true )
    const issuesResult = await apiClient.get( `issues/product/${pid}` );
    let totalIssued = 0;
    if ( issuesResult.status === 200 || issuesResult.status === 201 ) {
      const issuesData = issuesResult.data;
      issuesData.forEach( issue => {
        totalIssued += Number( issue.quantity );
      } )
      const issues = { data: issuesData, totalIssued: totalIssued };
      setInventory( prev => ( { ...prev, issues: issues } ) );
    }
    setLoading( false )
  }
  const fetchProductInventory = async ( pid ) => {
    setLoading( true )
    const inventoryResult = await apiClient.get( `inventory/product/${pid}` );
    if ( inventoryResult.status === 200 || inventoryResult.status === 201 ) {
      const invData = inventoryResult.data;
      let currentStock = 0;
      let totalReceived = 0;
      totalReceived = invData.reduce( ( acc, inv ) => acc + Number( inv.stockQuantity ), 0 );
      invData.forEach( inv => {
        currentStock += Number( inv.availableQuantity );
      } )
      setInventory( prev => ( { ...prev, totalReceived, currentStock } ) );
    }
    setLoading( false )
  }
  const fetchProductDetail = async ( pid ) => {
    setLoading( true )
    try {
      const response = await apiClient.get( `/products/${pid}` );
      if ( response.status === 200 ) {
        setProduct( response.data );
      }
      else {
        setProduct( null );
      }
    }
    catch ( e ) {
      console.log( "Error fetching product detail", e );
      setProduct( null );
      if ( e instanceof AxiosError && e.response && e.response.status === 401 ) {
        UTILS.signout();
        navigate( `/signin`, { replace: true, target: `/products/${id}` } );
      }
    }
    finally {
      setLoading( false );
    }
  }
  const loadData = async () => {
    await Promise.all( [ fetchProductInventory( id ), fetchIssuedInventory( id ), getRecentTransactions( id ) ] );
  }
  useEffect( () => {
    if ( !loginState ) {
      navigate( "/login", { replace: true, target: `/products/${id}` } );
      return;
    }
    loadData();
  }, [] )

  const totalSoldFromTransactions = recentTransactions.reduce( ( sum, transaction ) => {
    const productLines = ( transaction?.transactionProducts || [] ).filter( tp => tp.productId === id );
    const soldQty = productLines.reduce( ( qtySum, tp ) => qtySum + Number( tp.quantity || 0 ), 0 );
    return sum + soldQty;
  }, 0 );

  const totalSoldValueFromTransactions = recentTransactions.reduce( ( sum, transaction ) => {
    const productLines = ( transaction?.transactionProducts || [] ).filter( tp => tp.productId === id );
    const soldValue = productLines.reduce( ( valueSum, tp ) => valueSum + Number( tp.amount || 0 ), 0 );
    return sum + soldValue;
  }, 0 );

  return (
    <>
      <Header auth={loginState} />
      <section className="container shadow-md p-4 justify-start mt-8">
        <div className="w-full mb-4 flex justify-between items-start">
          <div>
            <p className="text-sm uppercase tracking-wide text-gray-500">Product Profile</p>
            <h2 className="text-2xl font-semibold text-gray-800">Product Details</h2>
          </div>
          <button className="p-1 rounded-md hover:bg-gray-100 transition-colors" onClick={() => { navigate( -1, { replace: true } ) }}><X className="text-primary-500 hover:text-secondary-500" /></button>
        </div>
        {loading ? <div className="w-full py-10 flex justify-center"><Loader className="animate-spin text-primary-500" /></div> :
          <div className="w-full rounded-xl border border-gray-200 bg-gradient-to-r from-white to-gray-50 p-4 md:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
              <div className="flex flex-col sm:flex-row lg:flex-col items-center lg:items-start gap-4">
                <img
                  id="preview"
                  alt={product?.productName}
                  src={product?.image ? product?.image : defaultProductImage}
                  className="h-28 w-28 rounded-full object-cover border-2 border-primary-200 shadow-sm mx-auto"
                />
                <div className="text-center sm:text-left lg:text-left">
                  <p className="text-xl font-semibold text-gray-800">{product?.productName || 'Unnamed Product'}</p>
                  <p className="text-sm text-gray-500 mt-1">{product?.description || 'No description available'}</p>
                  <p className="inline-block mt-3 text-xs font-semibold uppercase tracking-wide text-primary-700 bg-primary-100 px-2.5 py-1 rounded-full">{product?.packSize || 'No Pack Size'}</p>
                </div>
              </div>

              <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Stock Status</p>
                  <p id="reorder_level" className="mt-1">
                    {inventory?.currentStock > product?.reorderLevel
                      ? <span className="text-green-700 bg-green-300 py-1 px-2 rounded">In Stock</span>
                      : <span className="text-red-700 bg-red-300 py-1 px-2 rounded">Low Stock</span>}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Reorder Level</p>
                  <p id="pack_size" className="text-secondary-700 font-semibold mt-1">{UTILS.thousandSeparator( Number( product?.reorderLevel || 0 ) )}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Current Stock</p>
                  <p id="description" className="text-secondary-700 font-semibold mt-1">{UTILS.thousandSeparator( Number( inventory?.currentStock || 0 ) )}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Total Issued</p>
                  <p id="name" className="text-secondary-700 font-semibold mt-1">{UTILS.thousandSeparator( Number( inventory?.issues?.totalIssued || 0 ) )}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Total Sold</p>
                  <p id="name" className="text-secondary-700 font-semibold mt-1">{UTILS.thousandSeparator( totalSoldFromTransactions )}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Total Sold Value (TSH)</p>
                  <p className="text-secondary-700 font-semibold mt-1">{UTILS.thousandSeparator( Math.round( totalSoldValueFromTransactions ) )}</p>
                </div>
              </div>
            </div>
          </div>
        }
        <div className="text-2xl mt-8 mb-2">Recent Activities</div>
        {recentTransactions.length === 0 ? <div className="mb-2">No recent activities</div> :
          <><div className="grid md:grid-cols-6 bg-gray-200 p-2 font-bold ">
            <span>Date</span>
            <span>Type</span>
            <span>Customer</span>
            <span>Product</span>
            <span>Amount (TSH)</span>
            <span className="text-start">Document</span>
          </div>
            <div className="">{recentTransactions.map( ( transaction, idx ) => {
              return <TransactionItem className={`py-1 ${idx % 2 === 0 ? "bg-white" : "bg-gray-100"}`} data={transaction} key={transaction.id} onDelete={() => {}} onClick={() => { navigate( "/transactions/view/" + transaction.id, { replace: false, state: transaction } ) }} onEdit={() => { navigate( "/transactions/edit/" + transaction.id, { replace: false, state: transaction } ) }} />
            } )}</div></>}
      </section>
    </>
  )
}

export default ProductDetail;